import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { GitMerge, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { adminService } from "../../services/adminService";
import { showErrorToast } from "@/utils/errorHandler";

const MergeData = () => {
  const [isMerging, setIsMerging] = useState(false);
  const [lastMergeTime, setLastMergeTime] = useState<string | null>(
    localStorage.getItem("last_merge_at")
  );

  const handleMerge = async () => {
    try {
      setIsMerging(true);
      const response = await adminService.performDataMerge();
      if (response.success) {
        const now = new Date();
        const formattedDate = now.toLocaleString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
        setLastMergeTime(formattedDate);
        localStorage.setItem("last_merge_at", formattedDate);
        toast.success(
          response.message ??
            "Data synchronization and merging completed successfully!"
        );
      } else {
        toast.error(response.message ?? "Failed to merge data repository.");
      }
    } catch (error: any) {
      console.error("Merge error:", error);
      showErrorToast(error, "An error occurred during data merge.");
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <AdminLayout>
      <div className="animate-in fade-in space-y-6 duration-500">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-[#654321]">
              Data Management
            </h1>
          </div>
        </div>

        <Card className="overflow-hidden border-border/40 bg-gradient-to-br from-white to-stone-50 shadow-xl">
          <CardHeader className="border-b bg-card/50 px-6 py-8">
            <div className="flex items-center gap-4">
              <div className="rounded-xl bg-[#654321] p-3 text-white shadow-lg shadow-[#654321]/20">
                <GitMerge className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold text-[#4d3219]">
                  Merge Data Repository
                </CardTitle>
                <CardDescription className="mt-1 text-base text-stone-500">
                  Click the button below to start the intelligent merging
                  algorithm.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center space-y-8 p-12">
            <div className="group relative">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-[#654321] to-[#a67c52] opacity-25 blur transition duration-1000 group-hover:opacity-50 group-hover:duration-200" />
              <Button
                onClick={handleMerge}
                disabled={isMerging}
                size="lg"
                className="relative h-20 rounded-full bg-[#654321] px-12 text-xl font-bold text-white shadow-2xl transition-all duration-300 hover:scale-105 hover:bg-[#4d3219] disabled:opacity-80"
              >
                {isMerging ? (
                  <>
                    <Loader2 className="mr-3 h-8 w-8 animate-spin" />
                    Merging Records...
                  </>
                ) : (
                  <>
                    <GitMerge className="mr-3 h-8 w-8" />
                    Merge Data
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-4 pb-10 md:grid-cols-3">
          {[
            {
              label: "Last Merge",
              value: lastMergeTime ?? "Never",
              color: "text-stone-600",
            },
          ].map(stat => (
            <div
              key={stat.label}
              className="flex flex-col items-center justify-center rounded-xl border border-stone-100 bg-white p-4 shadow-sm"
            >
              <span className="mb-1 text-xs font-bold uppercase tracking-wider text-stone-400">
                {stat.label}
              </span>
              <span className={`text-lg font-bold ${stat.color}`}>
                {stat.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default MergeData;
